// components/ChatPanel.jsx
import React, { useEffect, useRef, useState } from "react";
import { FiChevronsRight } from "react-icons/fi";
import { BsArrowRightShort } from "react-icons/bs";
import { FaFacebookSquare, FaLinkedin, FaInstagramSquare } from "react-icons/fa";
import { Button, Image, Form } from "react-bootstrap";
import { MessagesAPI } from "../api/messages";
import MarkdownViewer from "./MarkdownViewer";
import HtmlTyper from "./HtmlTyper";
import HtmlViewer from "./HtmlViewer";
import TypingIndicator from "./TypingIndicator";
import Scroller from "./Scroller";
import ChatInput from "./ChatInput";
import magic from "../assets/images/magic.png";
import doc from "../assets/images/doc.png";
import user from "../assets/images/user.png";

const btnTxts = {
  story: 'Generate SRS',
  srs: 'Confirm',
  started: 'Build App',
}

function ChatPanel({
  step,
  query,
  onSend,
  onPrev,
  onNext,
  loading,
  messages,
  setQuery,
  signupData,
  setMessages,
  scrollerRef,
  signupErrors,
  onChangeInput,
  setWritingLoading,
}) {
  const hasRunRef = useRef(false);
  const chatInputRef = useRef(null);

  const navEntry = typeof performance !== "undefined" && performance.getEntriesByType ? performance.getEntriesByType("navigation")[0] : null;
  const isReload = navEntry?.type === "reload";

  const [editable, setEditable] = useState({});
  const [srsBtns, setSrsBtns] = useState(false);
  const [noOptions, setNoOptions] = useState({});
  const [submitBtns, setSubmitBtns] = useState({});
  const [showOptions, setShowOptions] = useState({});

  useEffect(() => {
    if (!isReload) return;
    if (step !== "story") return;
    if (hasRunRef.current) return;
    if (!Array.isArray(messages) || messages.length === 0) return;

    let changed = false;

    const nextEditable = {};
    const nextNoOptions = {};
    const nextSubmitBtns = {};
    const nextShowOptions = {};

    const updated = messages.map((m, i, arr) => {
      let out = m;

      const isAssistant = m.role !== "user";
      const hasOptions = Array.isArray(m.options) && m.options.length > 0;

      if (isAssistant && hasOptions) {
        const nextUserText = (arr[i + 1]?.text ?? arr[i + 1]?.message ?? "").split(",").map(s => s.trim()).filter(Boolean);

        if (nextUserText.length > 0) {
          const newOptions = m.options.map(opt => ({
            ...opt,
            checked: nextUserText.includes(opt.label.trim()) || !!opt.checked,
          }));

          const same = newOptions.length === m.options.length && newOptions.every((o, idx) => o.checked === m.options[idx].checked);
          if (!same) {
            out = { ...m, options: newOptions };
            changed = true;
          }
        }

        nextEditable[m.id] = false;
        nextNoOptions[m.id] = false;
        nextShowOptions[m.id] = true;
      } else {
        if (i === arr.length - 1) {
          nextNoOptions[m.id] = true;
        }
      }

      if (i === arr.length - 1) {
        nextSubmitBtns[m.id] = true;
      }

      if (m.allow_srs_generation) {
        setSrsBtns(true);
      }

      return out;
    });

    if (changed) {
      setMessages(updated);
    }

    setEditable(prev => ({ ...prev, ...nextEditable }));
    setNoOptions(prev => ({ ...prev, ...nextNoOptions }));
    setSubmitBtns(prev => ({ ...prev, ...nextSubmitBtns }));
    setShowOptions(prev => ({ ...prev, ...nextShowOptions }));

    hasRunRef.current = true;
    // Intentionally omit `messages` from deps so this runs exactly once on reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, messages, isReload]);

  const onTypeCompleted = async (message) => {
    setMessages(prev =>
      prev.map(m => (m.id === message.id ? { ...m, loaded: 1, options: message?.options || [] } : m))
    );
    setWritingLoading(false);
    chatInputRef.current?.focus();

    if (message.options.length > 0) {
      setShowOptions(prev => ({ ...prev, [message.id]: true }));
    } else {
      setNoOptions(prev => ({ ...prev, [message.id]: true }));
    }
    setSubmitBtns(prev => ({ ...prev, [message.id]: true }));

    if (message.allow_srs_generation) {
      setSrsBtns(true);
    }

    await MessagesAPI.updateMessage({ messageId: message.id, loaded: 1 });
  };

  const onOptionClick = (message, messageIndex, option, optionIndex) => {
    if (messageIndex < messages.length - 1) return;

    if (option.editable) {
      if (editable[message.id]) {
        setEditable(prev => ({ ...prev, [message.id]: false }));
      } else {
        setEditable(prev => ({ ...prev, [message.id]: true }));
      }
      return;
    }

    setMessages(prevMessages => prevMessages.map((message, mIndex) => {
      if (mIndex === messageIndex) {
        return {
          ...message,
          options: message.options.map((opt, oIndex) => oIndex === optionIndex ? { ...opt, checked: !opt.checked } : opt),
        };
      }
      return message;
    }));
  };

  const onSubmitOptions = (message) => {
    const selectedOptions = message.options.filter(opt => opt.checked).map(opt => opt.label).join(', ');
    const optionText = selectedOptions ? selectedOptions : '';
    const messageText = editable[message.id] ? query : '';
    let combinedText = '';
    if (optionText && messageText) {
      combinedText = `${optionText}, ${messageText}`;
    } else if (optionText) {
      combinedText = optionText;
    } else {
      combinedText = messageText;
    }
    if (!combinedText) return;
    onSend(combinedText);
    setEditable(prev => ({ ...prev, [message.id]: false }));
    setSubmitBtns(prev => ({ ...prev, [message.id]: false }));
  };

  const onSubmitQuery = (message) => {
    onSend(query);
    setNoOptions(prev => ({ ...prev, [message.id]: false }));
    setSubmitBtns(prev => ({ ...prev, [message.id]: false }));
  };

  return (
    <div className="assistant">
      {step === 'story' && (
        <>
          <div className="asst-head">
            <Image className="asst-img" src={magic} alt="SwiftDeploy AI" rounded />
            <div className="asst-title">SwiftDeploy AI Assistant</div>
          </div>
          <Scroller ref={scrollerRef} className="chat-area">
            {messages.map((message, index) => (
              <React.Fragment key={index}>
                {message.text && (
                  <>
                    {message.role === 'user' ? (
                      <div className="d-flex">
                        <div className="msg-bubble ms-auto">{message.text}</div>
                      </div>
                    ) : (
                      <div className="paper story">
                        {message.loaded === 0 ? (
                          <HtmlTyper text={message.text} chunk={2} speed={50} onComplete={() => onTypeCompleted(message)} />
                        ) : (
                          <HtmlViewer text={message.text} />
                        )}

                        {message?.options.length > 0 ? (
                          <>
                            {showOptions[message.id] && (
                              <div className="mt-3">
                                {message.options.map((option, oi) => (
                                  <Button key={oi} variant={`${option.checked || (option.editable && editable[message.id]) ? 'primary' : 'outline-primary'}`} size="sm" className="rounded-3 px-3 mb-2 me-2" disabled={!option.checked && index < messages.length - 1} onClick={() => onOptionClick(message, index, option, oi)}>{option.label}</Button>
                                ))}
                              </div>
                            )}
                            {editable[message.id] && (
                              <div className="mt-2">
                                <ChatInput
                                  query={query}
                                  onSend={() => { }}
                                  setQuery={setQuery}
                                  hideSendButton={true}
                                  chatPlaceholder="Please write..."
                                />
                              </div>
                            )}
                            {submitBtns[message.id] && (
                              <div className="mt-2 text-end">
                                <Button variant="primary" className="rounded-3 px-3" onClick={() => onSubmitOptions(message)}>
                                  Confirm <BsArrowRightShort />
                                </Button>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            {message.allow_srs_generation ? (
                              <div className="mt-3 text-end">
                                {srsBtns && (
                                  <button type="button" className="next-btn d-inline-flex align-items-center" onClick={onNext}>
                                    {btnTxts[step]} <FiChevronsRight />
                                  </button>
                                )}
                              </div>
                            ) : (
                              <>
                                {noOptions[message.id] && (
                                  <div className="mt-2">
                                    <ChatInput
                                      query={query}
                                      ref={chatInputRef}
                                      setQuery={setQuery}
                                      hideSendButton={true}
                                      chatPlaceholder="Please write..."
                                      onSend={() => onSubmitQuery(message)}
                                    />
                                  </div>
                                )}
                                {submitBtns[message.id] && (
                                  <div className="mt-2 text-end">
                                    <Button variant="primary" className="rounded-3 px-3" onClick={() => onSubmitQuery(message)}>
                                      Send <BsArrowRightShort />
                                    </Button>
                                  </div>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}
              </React.Fragment>
            ))}

            {loading && (
              <div className="d-flex justify-content-start mb-3">
                <TypingIndicator background="bg-light" />
              </div>
            )}
          </Scroller>
        </>
      )}

      {step === 'srs' && (
        <>
          <div className="asst-head">
            <Image className="asst-img small-img" src={doc} alt="SwiftDeploy AI" />
            <div className="asst-title">Software Requirement Specification (SRS) Preview</div>
          </div>
          <div className="srs-preview">
            {messages.some(m => m.role === 'assistant') && (
              <MarkdownViewer text={messages.find(m => m.role === 'assistant').text} />
            )}
          </div>
          <div className="d-flex justify-content-end pt-3">
            <button type="button" className="next-btn d-inline-flex align-items-center" onClick={onNext}>
              {btnTxts[step]} <FiChevronsRight />
            </button>
          </div>
        </>
      )}

      {step === 'started' && (
        <Form onSubmit={onNext}>
          <div className="asst-head">
            <button type="button" className="back-link bg-white border-0" onClick={(e) => onPrev('srs')}>
              <i className="icon-angle-left"></i> Back
            </button>
          </div>
          <div className="section mt-4 flex-fill overflow-x-hidden">
            <div className="title-row mb-4">
              <Image className="asst-img" src={user} alt="SwiftDeploy AI" />
              <div>
                <h5 className="h5-title">Personal Information</h5>
                <div className="subtext">Enter your personal information to continue.</div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 col-sm-12 mb-4">
                <label htmlFor="firstName" className="form-label">First Name: <small className="required">*</small></label>
                <input type="text" name="firstName" className={`form-control form-control-lg ${signupErrors.firstName ? 'is-invalid' : ''}`} value={signupData.firstName} onChange={onChangeInput} placeholder="e.g. Stephen" />
                {signupErrors.firstName && <div className="invalid-feedback">{signupErrors.firstName}</div>}
              </div>
              <div className="col-md-6 col-sm-12 mb-4">
                <label htmlFor="lastName" className="form-label">Last Name: <small className="required">*</small></label>
                <input type="text" name="lastName" className={`form-control form-control-lg ${signupErrors.lastName ? 'is-invalid' : ''}`} value={signupData.lastName} onChange={onChangeInput} placeholder="e.g. Hill" />
                {signupErrors.lastName && <div className="invalid-feedback">{signupErrors.lastName}</div>}
              </div>
              <div className="col-md-12 col-sm-12 mb-4">
                <label htmlFor="email" className="form-label">Business Email: <small className="required">*</small></label>
                <input type="email" name="email" className={`form-control form-control-lg ${signupErrors.email ? 'is-invalid' : ''}`} value={signupData.email} onChange={onChangeInput} placeholder="e.g. stephenhill@example.com" />
                {signupErrors.email && <div className="invalid-feedback">{signupErrors.email}</div>}
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-end pt-3">
            <button type="submit" className="next-btn d-inline-flex align-items-center">
              {btnTxts[step]} ✨
            </button>
          </div>
        </Form>
      )}

      {step === 'success' && (
        <div className="panel d-flex flex-column align-items-center justify-content-center">
          <div className="big-check" aria-hidden="true">
            <i className="icon-check"></i>
          </div>
          <h1 className="thanks">Thank you!</h1>
          <p className="desc fs-4">Your App is Being Created!</p>
          <p className="desc">We're currently working on building your app. You’ll receive an email with the link to access it as soon as it's ready. Thank you for your patience, and we look forward to helping you get started!</p>
          <div className="foot-note">Connect with us on</div>
          <div className="socials" role="group" aria-label="Social links">
            <a className="soc facebook" href="https://www.facebook.com/profile.php?id=61579139061068" target="_blank" rel="noreferrer">
              <FaFacebookSquare />
            </a>
            <a className="soc linkedin" href="https://www.linkedin.com/company/excellenc3" target="_blank" rel="noreferrer">
              <FaLinkedin />
            </a>
            <a className="soc instagram" href="https://www.instagram.com/excellenc3_aitransformation/" target="_blank" rel="noreferrer">
              <FaInstagramSquare />
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatPanel;